import type { SignupSchool, SignupSport } from '../../types/signup';

/**
 * Display helpers for self-serve signup. Pure, so every page quotes a price and names a school the
 * same way. Nothing here decides a price: it only formats the one the server sent.
 */

const currencyFormatters = new Map<string, Intl.NumberFormat>();

/**
 * Cached per currency: constructing an `Intl.NumberFormat` costs far more than calling `format` on
 * one (see "Reuse the cached Intl.DateTimeFormat" in web/CLAUDE.md).
 */
const currencyFormatter = (currency: string, wholeUnits: boolean): Intl.NumberFormat => {
  const key = `${currency}|${wholeUnits}`;
  let formatter = currencyFormatters.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: wholeUnits ? 0 : 2,
      maximumFractionDigits: wholeUnits ? 0 : 2,
    });
    currencyFormatters.set(key, formatter);
  }
  return formatter;
};

/** "$4,500". Whole dollars drop the cents; anything else keeps them. */
export const formatPrice = (cents: number, currency: string): string => {
  const wholeUnits = cents % 100 === 0;
  try {
    return currencyFormatter(currency || 'usd', wholeUnits).format(cents / 100);
  } catch {
    // An unrecognised currency code throws a RangeError. Show the number rather than nothing.
    return `${(cents / 100).toFixed(wholeUnits ? 0 : 2)} ${currency.toUpperCase()}`;
  }
};

/** "$4,500 / year" */
export const formatAnnualPrice = (cents: number, currency: string): string =>
  `${formatPrice(cents, currency)} / year`;

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/**
 * "February 1, 2027" from "2027-02-01", or null for anything else. Read straight off the digits:
 * the value is a plain date, so going through `Date` would show the day before west of UTC.
 */
export const formatPlainDate = (value: string | null | undefined): string | null => {
  const match = typeof value === 'string' ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(value) : null;
  if (!match) return null;
  const month = MONTHS[Number(match[2]) - 1];
  const day = Number(match[3]);
  if (!month || day < 1 || day > 31) return null;
  return `${month} ${day}, ${match[1]}`;
};

/** " Sign by February 1, 2027." for a 3-year offer with a last day, else "". Starts with a space. */
export const signBySuffix = (offerEndsOn: string | null | undefined): string => {
  const date = formatPlainDate(offerEndsOn);
  return date ? ` Sign by ${date}.` : '';
};

/**
 * "Or $4,050 a year with a 3-year agreement." for a plan that offers one, else null (not offered,
 * or an older API that does not send the 3-year price). With a last day to sign, adds
 * "Sign by February 1, 2027."
 */
export const threeYearPriceLine = (
  threeYearAnnualPriceCents: number | null | undefined,
  currency: string,
  offerEndsOn?: string | null
): string | null =>
  typeof threeYearAnnualPriceCents === 'number' && threeYearAnnualPriceCents > 0
    ? `Or ${formatPrice(threeYearAnnualPriceCents, currency)} a year with a 3-year agreement.${signBySuffix(offerEndsOn)}`
    : null;

/** "Ohio · D1 · Big Ten", skipping whatever the catalog does not know. */
export const describeSchool = (school: SignupSchool): string =>
  [school.state, school.level, school.conference].filter(Boolean).join(' · ');

export const sportLabel = (sport: SignupSport | string | null | undefined): string =>
  sport === 'softball' ? 'Softball' : sport === 'baseball' ? 'Baseball' : '';
